import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

// Static data - moved outside component to avoid recreation on each render
const currentStateData = {
  nodes: [
    { id: 'human', type: 'human', label: 'Human', category: 'human' },
    { id: 'apis', type: 'interface', label: 'APIs', category: 'api' },
    { id: 'docs', type: 'interface', label: 'Documentation', category: 'documentation' },
    { id: 'websites', type: 'interface', label: 'Websites', category: 'website' },
    { id: 'mobile', type: 'interface', label: 'Mobile Apps', category: 'mobile' },
    { id: 'databases', type: 'interface', label: 'Databases', category: 'database' },
    { id: 'cli', type: 'interface', label: 'Command Line', category: 'cli' },
    { id: 'files', type: 'interface', label: 'File Systems', category: 'other' },
  ],
  links: []
};

// Generate links from human to all interfaces
currentStateData.nodes.forEach(node => {
  if (node.id !== 'human') {
    currentStateData.links.push({ source: 'human', target: node.id });
  }
});

const futureStateData = {
  nodes: [
    { id: 'human', type: 'human', label: 'Human', level: 0 },
    { id: 'intent1', type: 'intent', label: 'Voice Interface', level: 1 },
    { id: 'intent2', type: 'intent', label: 'Chat Interface', level: 1 },
    { id: 'agent1', type: 'agent', label: 'Task Agent', level: 2 },
    { id: 'agent2', type: 'agent', label: 'Data Agent', level: 2 },
    { id: 'agent3', type: 'agent', label: 'Code Agent', level: 2 },
    { id: 'everything1', type: 'ai-backend', label: 'APIs', level: 3 },
    { id: 'everything2', type: 'ai-backend', label: 'Documentation', level: 3 },
    { id: 'everything3', type: 'ai-backend', label: 'Databases', level: 3 },
    { id: 'everything4', type: 'ai-backend', label: 'File Systems', level: 3 },
  ],
  links: [
    { source: 'human', target: 'intent1' },
    { source: 'human', target: 'intent2' },
    { source: 'intent1', target: 'agent1' },
    { source: 'intent2', target: 'agent2' },
    { source: 'intent2', target: 'agent3' },
    { source: 'agent1', target: 'agent2' },
    { source: 'agent2', target: 'agent3' },
    { source: 'agent1', target: 'agent3' },
    { source: 'agent1', target: 'everything1' },
    { source: 'agent1', target: 'everything4' },
    { source: 'agent2', target: 'everything2' },
    { source: 'agent2', target: 'everything3' },
    { source: 'agent3', target: 'everything1' },
    { source: 'agent3', target: 'everything2' },
  ]
};

const getNodeColor = (type, category) => {
  const colors = {
    human: '#2563eb',
    intent: '#059669',
    agent: '#ea580c',
    'ai-backend': '#6b7280',
    api: '#dc2626',
    documentation: '#16a34a',
    website: '#ca8a04',
    database: '#7c3aed',
    mobile: '#ec4899',
    cli: '#0891b2',
    other: '#64748b'
  };
  return colors[category] || colors[type] || colors.other;
};

const getNodeRadius = (type) => {
  const radii = {
    human: 24,
    intent: 18,
    agent: 16,
    'ai-backend': 14,
    interface: 14
  };
  return radii[type] || 14;
};

const drawCurrentState = (svg, data, width, height) => {
  svg.selectAll('*').remove();

  const simulation = d3.forceSimulation(data.nodes)
    .force('link', d3.forceLink(data.links).id(d => d.id).distance(150))
    .force('charge', d3.forceManyBody().strength(-500))
    .force('center', d3.forceCenter(width / 2, height / 2))
    .force('collision', d3.forceCollide().radius(d => getNodeRadius(d.type) + 20));

  // Create link group
  const linkGroup = svg.append('g').attr('class', 'links');

  // Create node group
  const nodeGroup = svg.append('g').attr('class', 'nodes');

  // Create label group
  const labelGroup = svg.append('g').attr('class', 'labels');

  // Draw links
  const links = linkGroup.selectAll('path')
    .data(data.links)
    .join('path')
    .attr('fill', 'none')
    .attr('stroke', '#cbd5e1')
    .attr('stroke-width', 2)
    .attr('stroke-opacity', 0)
    .attr('stroke-dasharray', '5,5');

  // Animate links
  links.transition()
    .delay((d, i) => i * 80)
    .duration(800)
    .attr('stroke-opacity', 0.4);

  // Create node elements
  const nodes = nodeGroup.selectAll('g')
    .data(data.nodes)
    .join('g')
    .attr('opacity', 0)
    .call(d3.drag()
      .on('start', dragstarted)
      .on('drag', dragged)
      .on('end', dragended));

  // Add outer ring for human
  nodes.filter(d => d.type === 'human')
    .append('circle')
    .attr('r', d => getNodeRadius(d.type) + 10)
    .attr('fill', 'none')
    .attr('stroke', d => getNodeColor(d.type, d.category))
    .attr('stroke-width', 2)
    .attr('stroke-opacity', 0.2);

  // Add main circles
  nodes.append('circle')
    .attr('r', d => getNodeRadius(d.type))
    .attr('fill', d => getNodeColor(d.type, d.category))
    .attr('stroke', '#fff')
    .attr('stroke-width', 3)
    .style('cursor', 'grab')
    .style('filter', 'drop-shadow(0 2px 8px rgba(0,0,0,0.1))')
    .on('mouseenter', function(event, d) {
      d3.select(this)
        .attr('r', getNodeRadius(d.type) * 1.15);
    })
    .on('mouseleave', function(event, d) {
      d3.select(this)
        .attr('r', getNodeRadius(d.type));
    });

  // Animate nodes
  nodes.transition()
    .delay((d, i) => i * 120)
    .duration(600)
    .attr('opacity', 1);

  // Add labels
  const labels = labelGroup.selectAll('text')
    .data(data.nodes)
    .join('text')
    .text(d => d.label)
    .attr('font-size', d => d.type === 'human' ? 13 : 11)
    .attr('font-weight', d => d.type === 'human' ? 600 : 400)
    .attr('font-family', 'system-ui, -apple-system, sans-serif')
    .attr('text-anchor', 'middle')
    .attr('fill', '#374151')
    .attr('dy', d => getNodeRadius(d.type) + 20)
    .attr('opacity', 0)
    .style('pointer-events', 'none');

  labels.transition()
    .delay((d, i) => i * 120 + 300)
    .duration(600)
    .attr('opacity', 1);

  // Simulation tick
  simulation.on('tick', () => {
    links.attr('d', d => {
      const dx = d.target.x - d.source.x;
      const dy = d.target.y - d.source.y;
      const dr = Math.sqrt(dx * dx + dy * dy) * 1.2;
      return `M${d.source.x},${d.source.y}A${dr},${dr} 0 0,1 ${d.target.x},${d.target.y}`;
    });

    nodes.attr('transform', d => `translate(${d.x},${d.y})`);
    labels.attr('transform', d => `translate(${d.x},${d.y})`);
  });

  function dragstarted(event, d) {
    if (!event.active) simulation.alphaTarget(0.3).restart();
    d.fx = d.x;
    d.fy = d.y;
    d3.select(this).style('cursor', 'grabbing');
  }

  function dragged(event, d) {
    d.fx = event.x;
    d.fy = event.y;
  }

  function dragended(event, d) {
    if (!event.active) simulation.alphaTarget(0);
    d.fx = null;
    d.fy = null;
    d3.select(this).style('cursor', 'grab');
  }
};

const drawFutureState = (svg, data, width, height) => {
  svg.selectAll('*').remove();

  const levelHeight = height / 4.5;
  const positionedNodes = data.nodes.map(node => {
    const nodesAtLevel = data.nodes.filter(n => n.level === node.level);
    const indexAtLevel = nodesAtLevel.indexOf(node);
    const levelWidth = width / (nodesAtLevel.length + 1);

    return {
      ...node,
      x: levelWidth * (indexAtLevel + 1),
      y: levelHeight * (node.level + 0.6)
    };
  });

  // Layer backgrounds
  const layers = [
    { level: 0, label: 'Human Layer', color: '#eff6ff' },
    { level: 1, label: 'Intent Layer', color: '#f0fdf4' },
    { level: 2, label: 'Agent Layer', color: '#fff7ed' },
    { level: 3, label: 'System Layer', color: '#f9fafb' }
  ];

  const layerGroup = svg.append('g').attr('class', 'layers');

  layerGroup.selectAll('rect')
    .data(layers)
    .join('rect')
    .attr('x', 0)
    .attr('y', d => levelHeight * d.level + 10)
    .attr('width', width)
    .attr('height', levelHeight)
    .attr('fill', d => d.color)
    .attr('rx', 8)
    .attr('opacity', 0)
    .transition()
    .duration(800)
    .attr('opacity', 0.5);

  layerGroup.selectAll('text')
    .data(layers)
    .join('text')
    .attr('x', 16)
    .attr('y', d => levelHeight * d.level + 32)
    .text(d => d.label)
    .attr('font-size', 11)
    .attr('font-weight', 600)
    .attr('fill', '#9ca3af')
    .attr('font-family', 'system-ui, -apple-system, sans-serif')
    .attr('opacity', 0)
    .transition()
    .duration(800)
    .attr('opacity', 0.7);

  // Create curved path
  const linkPath = (d) => {
    const sourceNode = positionedNodes.find(n => n.id === d.source);
    const targetNode = positionedNodes.find(n => n.id === d.target);
    const sx = sourceNode.x, sy = sourceNode.y;
    const tx = targetNode.x, ty = targetNode.y;
    const midY = (sy + ty) / 2;
    return `M${sx},${sy} C${sx},${midY} ${tx},${midY} ${tx},${ty}`;
  };

  // Draw links
  const linkGroup = svg.append('g').attr('class', 'links');

  const links = linkGroup.selectAll('path')
    .data(data.links)
    .join('path')
    .attr('d', linkPath)
    .attr('fill', 'none')
    .attr('stroke', d => {
      const sourceNode = positionedNodes.find(n => n.id === d.source);
      const targetNode = positionedNodes.find(n => n.id === d.target);
      return sourceNode.level === targetNode.level ? '#f97316' : '#94a3b8';
    })
    .attr('stroke-width', d => {
      const sourceNode = positionedNodes.find(n => n.id === d.source);
      const targetNode = positionedNodes.find(n => n.id === d.target);
      return sourceNode.level === targetNode.level ? 2.5 : 2;
    })
    .attr('stroke-opacity', 0)
    .attr('stroke-linecap', 'round');

  // Animate links
  links.each(function() {
    const path = d3.select(this);
    const length = this.getTotalLength();

    path
      .attr('stroke-dasharray', `${length} ${length}`)
      .attr('stroke-dashoffset', length)
      .attr('stroke-opacity', 0.6);
  });

  links.transition()
    .delay((d, i) => 600 + i * 60)
    .duration(1000)
    .attr('stroke-dashoffset', 0);

  // Draw nodes
  const nodeGroup = svg.append('g').attr('class', 'nodes');

  const nodes = nodeGroup.selectAll('g')
    .data(positionedNodes)
    .join('g')
    .attr('transform', d => `translate(${d.x},${d.y})`)
    .attr('opacity', 0);

  // Outer glow for key nodes
  nodes.filter(d => d.type === 'human' || d.type === 'intent')
    .append('circle')
    .attr('r', d => getNodeRadius(d.type) + 8)
    .attr('fill', 'none')
    .attr('stroke', d => getNodeColor(d.type))
    .attr('stroke-width', 2)
    .attr('stroke-opacity', 0.15);

  // Main circles
  nodes.append('circle')
    .attr('r', d => getNodeRadius(d.type))
    .attr('fill', d => getNodeColor(d.type))
    .attr('stroke', '#fff')
    .attr('stroke-width', 3)
    .style('cursor', 'pointer')
    .style('filter', 'drop-shadow(0 3px 10px rgba(0,0,0,0.12))')
    .on('mouseenter', function(event, d) {
      d3.select(this)
        .attr('r', getNodeRadius(d.type) * 1.2);

      // Highlight connected links
      linkGroup.selectAll('path')
        .attr('stroke-opacity', function(link) {
          const sourceNode = positionedNodes.find(n => n.id === link.source);
          const targetNode = positionedNodes.find(n => n.id === link.target);
          return (sourceNode.id === d.id || targetNode.id === d.id) ? 1 : 0.2;
        });
    })
    .on('mouseleave', function(event, d) {
      d3.select(this)
        .attr('r', getNodeRadius(d.type));

      linkGroup.selectAll('path')
        .attr('stroke-opacity', 0.6);
    });

  // Animate nodes
  nodes.transition()
    .delay((d, i) => 400 + i * 80)
    .duration(700)
    .attr('opacity', 1);

  // Labels
  const labelGroup = svg.append('g').attr('class', 'labels');

  labelGroup.selectAll('text')
    .data(positionedNodes)
    .join('text')
    .attr('x', d => d.x)
    .attr('y', d => d.y + getNodeRadius(d.type) + 20)
    .text(d => d.label)
    .attr('font-size', d => d.type === 'human' ? 13 : 11)
    .attr('font-weight', d => d.type === 'human' ? 600 : 400)
    .attr('font-family', 'system-ui, -apple-system, sans-serif')
    .attr('text-anchor', 'middle')
    .attr('fill', '#374151')
    .attr('opacity', 0)
    .transition()
    .delay((d, i) => 800 + i * 80)
    .duration(600)
    .attr('opacity', 1);
};

const InterfaceTransitionGraphs = ({ graphType = 'current' }) => {
  const svgRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const animationRef = useRef(null);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.attr('width', dimensions.width).attr('height', dimensions.height);

    // Deep clone data to avoid D3 mutating the module-level objects
    if (graphType === 'current') {
      const data = JSON.parse(JSON.stringify(currentStateData));
      drawCurrentState(svg, data, dimensions.width, dimensions.height);
    } else {
      const data = JSON.parse(JSON.stringify(futureStateData));
      drawFutureState(svg, data, dimensions.width, dimensions.height);
    }

    const currentAnimationRef = animationRef.current;
    return () => {
      if (currentAnimationRef) {
        cancelAnimationFrame(currentAnimationRef);
      }
    };
  }, [graphType, dimensions]);

  useEffect(() => {
    const handleResize = () => {
      const container = svgRef.current?.parentElement;
      if (container) {
        const containerWidth = container.clientWidth;
        setDimensions({
          width: Math.min(800, containerWidth - 32),
          height: 600
        });
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="w-full" style={{ minHeight: '600px' }}>
      <svg
        ref={svgRef}
        className="w-full h-auto"
        style={{ maxWidth: '100%', height: 'auto' }}
      />
    </div>
  );
};

export default InterfaceTransitionGraphs;
